import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { DashboardLayout } from '../../../layouts/DashboardLayout';
import { getWaEventSettings, updateWaEventSetting, resetWaEventSetting } from '../../../services/api';
import { toast } from 'sonner';
import {
  MessageCircle, Settings, Inbox, Bot, FileText, Zap, Bell, Users,
  ToggleLeft, ToggleRight, Save, RotateCcw, X, Info, Eye
} from 'lucide-react';

const NAV_ITEMS = [
  { label: 'Cuentas', path: '/superadmin/whatsapp', icon: MessageCircle },
  { label: 'Inbox', path: '/superadmin/whatsapp/inbox', icon: Inbox },
  { label: 'Auto-respuestas', path: '/superadmin/whatsapp/auto-replies', icon: Bot },
  { label: 'Plantillas', path: '/superadmin/whatsapp/templates', icon: FileText },
  { label: 'Resp. Rapidas', path: '/superadmin/whatsapp/quick-replies', icon: Zap },
  { label: 'Contactos', path: '/superadmin/whatsapp/contacts', icon: Users },
  { label: 'Eventos', path: '/superadmin/whatsapp/event-settings', icon: Settings },
  { label: 'Notificaciones', path: '/superadmin/whatsapp/notifications', icon: Bell },
];

const RECIPIENT_COLORS = {
  vendedor: 'bg-blue-50 text-blue-700',
  comprador: 'bg-green-50 text-green-700',
  'nuevo usuario': 'bg-purple-50 text-purple-700',
};

const WhatsAppEventSettings = () => {
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingKey, setEditingKey] = useState(null);
  const [editTemplate, setEditTemplate] = useState('');
  const [saving, setSaving] = useState(false);
  const [previewKey, setPreviewKey] = useState(null);

  const loadEvents = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getWaEventSettings();
      setEvents(res.data?.event_settings || []);
    } catch { toast.error('Error cargando configuracion de eventos'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  const handleToggle = async (event) => {
    try {
      const res = await updateWaEventSetting(event.event_key, { is_enabled: !event.is_enabled });
      const updated = res.data?.event_setting;
      if (updated) {
        setEvents(prev => prev.map(e => e.event_key === updated.event_key ? updated : e));
        toast.success(`${updated.event_label}: ${updated.is_enabled ? 'Activado' : 'Desactivado'}`);
      }
    } catch { toast.error('Error actualizando'); }
  };

  const startEditing = (event) => {
    setEditingKey(event.event_key);
    setEditTemplate(event.template_text || '');
  };

  const cancelEditing = () => {
    setEditingKey(null);
    setEditTemplate('');
  };

  const saveTemplate = async (eventKey) => {
    setSaving(true);
    try {
      const res = await updateWaEventSetting(eventKey, { template_text: editTemplate });
      const updated = res.data?.event_setting;
      if (updated) {
        setEvents(prev => prev.map(e => e.event_key === updated.event_key ? updated : e));
        toast.success('Plantilla guardada');
        setEditingKey(null);
      }
    } catch { toast.error('Error guardando plantilla'); }
    finally { setSaving(false); }
  };

  const handleReset = async (eventKey) => {
    if (!window.confirm('Restaurar la plantilla al texto original?')) return;
    try {
      const res = await resetWaEventSetting(eventKey);
      const updated = res.data?.event_setting;
      if (updated) {
        setEvents(prev => prev.map(e => e.event_key === updated.event_key ? updated : e));
        if (editingKey === eventKey) {
          setEditTemplate(updated.template_text || '');
        }
        toast.success('Plantilla restaurada');
      }
    } catch { toast.error('Error restaurando'); }
  };

  // Simple preview: replace {{var}} with example values
  const getPreview = (event) => {
    let text = event.template_text || '';
    const examples = {
      order_number: 'MKT-20260303-00001',
      total: '350.00',
      items_text: 'Hamburguesa x2, Papas x1',
      item_count: '3',
      customer_name: 'Juan Perez',
      customer_phone: '+521234567890',
      business_name: 'Mi Restaurante',
      delivery_type: 'delivery',
      payment_method: 'tarjeta',
      status_label: 'confirmado',
      status: 'confirmed',
      serial: 'TEL-ABC12-DEF34-GHI56',
      product_code: 'comanda-pro',
      folio: 'V-00123',
      branch_name: 'Sucursal Centro',
    };
    text = text.replace(/\{\{(\w+)\}\}/g, (_, key) => examples[key] || `{{${key}}}`);
    return text;
  };

  return (
    <DashboardLayout>
      <div className="max-w-7xl mx-auto">
        {/* Sub-nav */}
        <div className="flex items-center gap-1 overflow-x-auto pb-1 border-b border-slate-200 mb-4">
          {NAV_ITEMS.map(item => {
            const Icon = item.icon;
            const active = item.path === '/superadmin/whatsapp/event-settings';
            return (
              <button key={item.path} onClick={() => navigate(item.path)}
                className={`flex items-center gap-1.5 px-3 py-2 text-sm rounded-lg whitespace-nowrap transition-colors ${
                  active ? 'bg-green-100 text-green-700 font-medium' : 'text-slate-500 hover:bg-slate-100 hover:text-slate-700'
                }`}>
                <Icon className="w-4 h-4" /> {item.label}
              </button>
            );
          })}
        </div>

        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 rounded-xl flex items-center justify-center">
              <Settings className="w-5 h-5 text-green-700" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-slate-900">Eventos de Notificacion</h1>
              <p className="text-sm text-slate-500">Activa/desactiva y personaliza las notificaciones por WhatsApp</p>
            </div>
          </div>
        </div>

        {/* Info box */}
        <div className="bg-blue-50 border border-blue-200 rounded-xl px-4 py-3 mb-6 flex items-start gap-3">
          <Info className="w-5 h-5 text-blue-500 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-blue-800">
            <p className="font-medium mb-1">Variables disponibles</p>
            <p>Usa <code className="bg-blue-100 px-1 rounded text-xs">{'{{variable}}'}</code> en el texto de la plantilla. Las variables se reemplazan automaticamente con los datos reales al enviar la notificacion.</p>
          </div>
        </div>

        {/* Events list */}
        {loading ? (
          <div className="text-center py-12 text-slate-400">Cargando...</div>
        ) : (
          <div className="space-y-4">
            {events.map(event => (
              <div key={event.event_key}
                className={`bg-white rounded-xl border transition-colors ${
                  event.is_enabled ? 'border-slate-200' : 'border-slate-100 opacity-70'
                }`}
              >
                {/* Event header */}
                <div className="px-5 py-4 flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-semibold text-slate-900">{event.event_label}</h3>
                      <span className={`text-[10px] font-medium px-2 py-0.5 rounded-full ${RECIPIENT_COLORS[event.recipient] || 'bg-slate-100 text-slate-600'}`}>
                        {event.recipient}
                      </span>
                      <span className="text-[10px] font-mono text-slate-400 bg-slate-50 px-2 py-0.5 rounded">
                        {event.event_key}
                      </span>
                    </div>
                    <p className="text-sm text-slate-500">{event.description}</p>
                  </div>
                  <button onClick={() => handleToggle(event)}
                    className={`flex-shrink-0 ml-4 p-1 rounded-lg transition-colors ${
                      event.is_enabled ? 'text-green-600 hover:bg-green-50' : 'text-slate-400 hover:bg-slate-100'
                    }`}
                    title={event.is_enabled ? 'Desactivar' : 'Activar'}
                  >
                    {event.is_enabled
                      ? <ToggleRight className="w-8 h-8" />
                      : <ToggleLeft className="w-8 h-8" />
                    }
                  </button>
                </div>

                {/* Template section */}
                <div className="px-5 pb-4 border-t border-slate-100 pt-3">
                  {/* Variables */}
                  <div className="flex flex-wrap gap-1.5 mb-3">
                    <span className="text-xs font-medium text-slate-500 mr-1">Variables:</span>
                    {(event.available_variables || []).map(v => (
                      <button key={v} onClick={() => {
                        if (editingKey === event.event_key) {
                          setEditTemplate(prev => prev + `{{${v}}}`);
                        }
                      }}
                        className="text-[11px] font-mono bg-slate-100 text-slate-600 px-2 py-0.5 rounded hover:bg-slate-200 cursor-pointer">
                        {`{{${v}}}`}
                      </button>
                    ))}
                  </div>

                  {editingKey === event.event_key ? (
                    <>
                      <textarea
                        value={editTemplate}
                        onChange={e => setEditTemplate(e.target.value)}
                        rows={4}
                        className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm font-mono resize-none focus:ring-2 focus:ring-green-500 focus:border-green-500"
                        placeholder="Escribe la plantilla del mensaje..."
                      />
                      <div className="flex items-center justify-between mt-2">
                        <div className="flex gap-2">
                          <button onClick={() => saveTemplate(event.event_key)} disabled={saving}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50">
                            <Save className="w-3.5 h-3.5" /> {saving ? 'Guardando...' : 'Guardar'}
                          </button>
                          <button onClick={cancelEditing}
                            className="flex items-center gap-1.5 px-3 py-1.5 text-sm text-slate-600 hover:bg-slate-100 rounded-lg">
                            <X className="w-3.5 h-3.5" /> Cancelar
                          </button>
                        </div>
                        <button onClick={() => handleReset(event.event_key)}
                          className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600">
                          <RotateCcw className="w-3.5 h-3.5" /> Restaurar original
                        </button>
                      </div>
                    </>
                  ) : (
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex-1 bg-slate-50 rounded-lg px-3 py-2.5 font-mono text-sm text-slate-700 whitespace-pre-wrap">
                        {event.template_text || '(sin plantilla)'}
                      </div>
                      <div className="flex flex-col gap-1 flex-shrink-0">
                        <button onClick={() => startEditing(event)}
                          className="p-1.5 text-slate-400 hover:text-green-600 hover:bg-green-50 rounded-lg" title="Editar plantilla">
                          <Settings className="w-4 h-4" />
                        </button>
                        <button onClick={() => setPreviewKey(previewKey === event.event_key ? null : event.event_key)}
                          className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg" title="Vista previa">
                          <Eye className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  )}

                  {/* Preview */}
                  {previewKey === event.event_key && (
                    <div className="mt-3 bg-green-50 border border-green-200 rounded-lg px-4 py-3">
                      <p className="text-[10px] font-medium text-green-600 mb-1.5 uppercase tracking-wider">Vista previa</p>
                      <div className="bg-white rounded-xl px-4 py-3 text-sm text-slate-800 whitespace-pre-wrap shadow-sm border border-green-100">
                        {getPreview(event)}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </DashboardLayout>
  );
};

export default WhatsAppEventSettings;
